import React from 'react';
import {
  Box,
  Card,
  Typography,
  useTheme,
  CircularProgress,
  LinearProgress,
} from '@mui/material';
import {
  CreditCard as CreditCardIcon,
  Money as CashIcon,
  AccountBalance as OtherIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { glassStyle } from '../theme/luxuryTheme';
import { usePaymentAnalysis } from '../hooks/useAnalytics';

interface PaymentMethodsProps {
  date?: string;
  storeId?: string;
}

const PaymentMethods: React.FC<PaymentMethodsProps> = ({ date, storeId }) => {
  const theme = useTheme();
  const { data, isLoading, error } = usePaymentAnalysis(date, storeId);

  if (isLoading) {
    return (
      <Card sx={{ ...glassStyle, p: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
        <CircularProgress sx={{ color: theme.palette.primary.main }} />
      </Card>
    );
  }

  if (error || !data) {
    return (
      <Card sx={{ ...glassStyle, p: 4 }}>
        <Typography color="error">Failed to load payment data</Typography>
      </Card>
    );
  }

  // Handle both old and new data formats
  let paymentTypes: Array<{
    type: string;
    amount: number;
    percentage: number;
    count?: number;
    icon: React.ReactElement;
    color: string;
  }> = [];
  
  if (data.payments && Array.isArray(data.payments)) {
    // New format from hybrid API
    paymentTypes = data.payments.map((payment: any) => {
      let icon = <OtherIcon />;
      let color = theme.palette.warning.main;
      
      if (payment.method?.toLowerCase().includes('credit') || payment.method?.toLowerCase().includes('card')) {
        icon = <CreditCardIcon />;
        color = theme.palette.primary.main;
      } else if (payment.method?.toLowerCase().includes('cash')) {
        icon = <CashIcon />;
        color = '#52c41a';
      }
      
      return {
        type: payment.method || 'Unknown',
        amount: payment.amount || 0,
        percentage: payment.percentage || 0,
        count: payment.count || 0,
        icon,
        color,
      };
    });
  } else {
    // Old format fallback
    paymentTypes = [
      {
        type: 'Credit/Debit',
        amount: data.summary?.totalCredit || 0,
        percentage: data.percentages?.credit || 0,
        icon: <CreditCardIcon />,
        color: theme.palette.primary.main,
      },
      {
        type: 'Cash',
        amount: data.summary?.totalCash || 0,
        percentage: data.percentages?.cash || 0,
        icon: <CashIcon />,
        color: '#52c41a',
      },
      {
        type: 'Other',
        amount: data.summary?.totalOther || 0,
        percentage: data.percentages?.other || 0,
        icon: <OtherIcon />,
        color: theme.palette.warning.main,
      },
    ];
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <Card sx={{ ...glassStyle, p: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
          Payment Methods
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {paymentTypes.map((payment: any) => (
            <Box key={payment.type}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 40,
                    height: 40,
                    borderRadius: '10px',
                    backgroundColor: `${payment.color}20`,
                    color: payment.color,
                    mr: 2,
                  }}
                >
                  {payment.icon}
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {payment.type}
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: payment.color }}>
                    ${payment.amount.toFixed(2)}
                  </Typography>
                </Box>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 700,
                    color: payment.color,
                  }}
                >
                  {payment.percentage.toFixed(1)}%
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={payment.percentage}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: payment.color,
                    borderRadius: 4,
                  },
                }}
              />
            </Box>
          ))}
        </Box>

        <Box sx={{ mt: 3, pt: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Total Transactions
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {data.total ? Math.round(data.total / 50) : data.count || 0}
            </Typography>
          </Box>
        </Box>
      </Card>
    </motion.div>
  );
};

export default PaymentMethods;